import axios from "axios";

// A body is the {success,data,error,meta} envelope (docs/api-response-contract.md)
// only if it carries all of `success`/`data`/`error` as real keys. The
// still-current "newer routes" shape (`{success, ...fields}` / `{success:
// false, error: "<string>"}`) has `success` but no `data` key and a string
// `error`, so it fails this check and falls through untouched, same as the
// fully-legacy shapes that have no `success` key at all.
function isEnvelope(body) {
  return (
    body !== null &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    typeof body.success === "boolean" &&
    Object.prototype.hasOwnProperty.call(body, "data") &&
    Object.prototype.hasOwnProperty.call(body, "error")
  );
}

// Unwraps the response envelope so existing callers (`const {data} = await
// axiosAuth.get(...)`) keep working unchanged whether the server has
// migrated a given route to the envelope yet or not.
export function unwrapEnvelope(response) {
  const body = response.data;
  if (isEnvelope(body) && body.success === true) {
    response.data = body.data;
  }
  return response;
}

// Normalizes an envelope error onto the thrown axios error
// (code/message/details/requestId) without touching non-envelope error
// bodies, so pre-migration routes keep throwing exactly what they throw
// today. Mutates and rejects with the original axios error (rather than a
// new one) so existing `error.response.status` checks keep working.
export function normalizeEnvelopeError(error) {
  const response = error.response;
  const body = response && response.data;

  if (isEnvelope(body) && body.success === false) {
    const envelopeError = body.error || {};
    error.code = envelopeError.code || null;
    error.message = envelopeError.message || error.message;
    error.details =
      envelopeError.details !== undefined ? envelopeError.details : null;
    error.requestId =
      envelopeError.request_id ||
      (body.meta && body.meta.request_id) ||
      (response.headers && response.headers["x-request-id"]) ||
      null;
  }

  return Promise.reject(error);
}

function applyEnvelopeInterceptor(instance) {
  instance.interceptors.response.use(unwrapEnvelope, normalizeEnvelopeError);
}

// For API routes that require credentials (cookies/auth)
export const axiosAuth = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// For connector or public routes that do NOT require credentials
export const axiosNoAuth = axios.create({
  baseURL: process.env.REACT_APP_CONNECTOR_ROUTE,
  withCredentials: false,
});

applyEnvelopeInterceptor(axiosAuth);
applyEnvelopeInterceptor(axiosNoAuth);
